import { roughLocation } from './lib/rough-location.ts';
import { warningIconSvg } from './warning-icons.tsx';
/**
 * The shared vocabulary of the map: what kinds of point exist and how each one
 * looks.
 *
 * In its own module, with no MapLibre import, so the legend and the types can
 * be used without pulling a megabyte of rendering library into the main bundle
 * — which is exactly what a static re-export from the implementation file did.
 */

/**
 * Where the base map sits when nothing else decides.
 *
 * The map is the operational canvas (`design-dashboard.md` §8), so it is drawn
 * even before a single building has coordinates — an empty grey box is an
 * outage, not an empty state. A caller that knows better (a placed site, an
 * estate of them) passes a real centre and this is never reached; it is the
 * floor, deliberately a country-scale view of the rollout-wave-1 geography
 * rather than a street nobody chose. Both map engines share it so the fallback
 * looks identical whichever renders.
 */
export const DEFAULT_CENTER = { latitude: -37.8136, longitude: 144.9631 } as const;

let nearDefault: { latitude: number; longitude: number } | undefined;

/**
 * Where an org with nothing placed opens its map: near the person, from the
 * city their browser's time zone names (`lib/rough-location.ts` — no prompt, no
 * network), so a London admin does not open on Melbourne (Trav 2026-09-30,
 * "launching globally"). `DEFAULT_CENTER` only when the zone names nowhere.
 * Computed once per page and cached, so the one persistent map never gets a
 * new centre object mid-session (the no-remount rule).
 */
export function defaultCenter(): { latitude: number; longitude: number } {
  if (!nearDefault) {
    const rough = roughLocation();
    nearDefault = rough ? { latitude: rough.latitude, longitude: rough.longitude } : DEFAULT_CENTER;
  }
  return nearDefault;
}

export interface MapPoint {
  id: string;
  latitude: number;
  longitude: number;
  label: string;
  /**
   * Drives colour *and* the glyph, because colour is never the only carrier of
   * state — a WCAG requirement and the greyscale test in
   * `docs/design-dashboard.md` §5.
   */
  kind:
    | 'site'
    | 'alerting'
    | 'assembly'
    | 'clear'
    | 'sweeping'
    | 'unswept'
    | 'blocked'
    /** A grab handle on a circle's edge — drag it to resize the area (the wizard). */
    | 'handle'
    /** A corner of an outline being drawn — shown from the first tap. */
    | 'vertex'
    /**
     * An agency's warning (or a counted cluster of them) — the kind's glyph in
     * a tier disc, the phone's `WarningPin`. Carries `warning`.
     */
    | 'warning';
  /**
   * The pin can be pressed and dragged (the wizard's building and assembly
   * points, the building page's pin). Both engines honour it through the
   * shared press-and-drag in `lib/marker-drag.ts`, with a grab cursor.
   */
  draggable?: boolean;
  /**
   * Drawn faded — context, not the thing being worked on (the wizard's saved
   * assembly points while another one is being edited).
   */
  dim?: boolean;
  /**
   * Context, never part of the camera's framing: the estate frames its
   * buildings, and a warning pin that came into view must not fling the camera
   * out to fit a storm three states away (both engines honour it).
   */
  ambient?: boolean;
  /** For `kind: 'warning'`: the tier, the kind's glyph, and a cluster's count. */
  warning?: { tier: WarningTier; icon: string; count?: number };
}

/**
 * A warning's tier on the dashboard map. The phone's two tiers (Extreme/Severe
 * red, the rest amber) shifted one rung down, because red here is reserved for
 * this org's own live emergency (`design-dashboard.md` §5): `severe` takes the
 * attention orange, `advice` the yellow — the Australian Warning System's Watch
 * and Act / Advice colours, with its red left to the activation path.
 */
export type WarningTier = 'severe' | 'advice';

/**
 * A warning tier's paint, as CSS values for the DOM pin. The yellow does not
 * flip with the theme, so its glyph is the light theme's ink in both — a fixed
 * dark that stays legible on yellow either way.
 */
export function warningPinPaint(tier: WarningTier): { background: string; glyph: string } {
  return tier === 'severe'
    ? { background: 'var(--attention)', glyph: '#ffffff' }
    : { background: 'var(--firstlight)', glyph: '#0e1524' };
}

export const STYLE = {
  site: { colour: 'var(--ink)', glyph: '■' },
  // A building with something running. The only place red appears on a map,
  // and the glyph carries it too — this has to read in greyscale like every
  // other state (§5).
  alerting: { colour: 'var(--alert)', glyph: '◆' },
  // Where people are safe: the safe green, like the ✓ of a cleared area, so
  // an evacuation point reads as the good place to be and never as a control.
  assembly: { colour: 'var(--safe)', glyph: '▲' },
  clear: { colour: 'var(--safe)', glyph: '✓' },
  sweeping: { colour: 'var(--attention)', glyph: '◐' },
  unswept: { colour: 'var(--ink-3)', glyph: '○' },
  blocked: { colour: 'var(--alert)', glyph: '✕' },
  handle: { colour: 'var(--accent)', glyph: '↔' },
  vertex: { colour: 'var(--accent)', glyph: '' },
  // The legend's swatch only; the pin itself is drawn by `warningMarkerElement`.
  warning: { colour: 'var(--attention)', glyph: '!' },
} as const;

/**
 * The inner HTML of a marker: the coloured glyph, and — for a **resting**
 * building (`site`) only — a slow, low-opacity "we are monitoring" pulse
 * radiating out behind it, echoing the mobile app's ripple in its calm form.
 *
 * It is the nothing-is-happening state: a building with something running is
 * passed as `alerting` (red, no pulse), and the live view's own emergency
 * treatment takes over. The pulse never uses red (`design-dashboard.md` §5 —
 * red is the live-emergency reserve); it takes the brand accent at low opacity.
 * The animation and its `prefers-reduced-motion` static fallback live in
 * `styles.css` (`.map-monitor-pulse`); shared here so both map engines (MapKit
 * and Google Maps) draw an identical marker.
 */
export function markerGlyphHtml(kind: MapPoint['kind']): string {
  const style = STYLE[kind];
  // A corner: a small dot with a paper ring, so each tap shows where it landed.
  if (kind === 'vertex')
    return `<span aria-hidden style="position:relative;display:block;width:12px;height:12px;border-radius:50%;background:${style.colour};border:2px solid var(--paper);box-sizing:border-box;box-shadow:0 0 0 1px ${style.colour}"></span>`;
  // Absolutely positioned to fill the 22px box, so the disc's centre IS the
  // box's centre whatever the line box does — an inline-flex span sat on the
  // baseline and drifted a few pixels off the coordinate its area circle is
  // drawn around (Trav, 2026-10-13). A paper ring so it reads over any area fill.
  const glyph = `<span aria-hidden style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;width:22px;height:22px;border-radius:50%;background:${style.colour};color:#fff;font-size:11px;line-height:1;border:2px solid var(--paper);box-sizing:border-box;box-shadow:0 1px 3px rgba(0,0,0,.25)">${style.glyph}</span>`;
  if (kind !== 'site') return glyph;
  return `<span style="position:relative;display:inline-flex;width:22px;height:22px"><span class="map-monitor-pulse" aria-hidden="true"></span>${glyph}</span>`;
}

/**
 * The marker's hotspot, in CSS pixels from the element's top-left — the point
 * the coordinate sits under. A building/site is anchored on the centre of its
 * 28px landmark avatar (14); every other kind on the centre of its 22px glyph
 * (11). Both engines read it (Google translates the element by it, MapKit
 * derives `anchorOffset` from it), so a marker lands on the same spot whichever
 * engine draws it.
 */
export function markerAnchor(kind: MapPoint['kind']): number {
  if (kind === 'vertex') return 6;
  return kind === 'site' || kind === 'warning' ? 14 : 11;
}

/**
 * The DOM a marker is drawn from — the design system owns it, both map engines
 * only place it. A building/site reads as an Apple-style landmark: a round
 * avatar with a calm label centred *beneath* it (the "PARRAMATTA" treatment the
 * product owner pointed at). Every other kind keeps the compact glyph-plus-chip
 * pin, so assembly points and the live sweep states stay visually distinct from
 * the buildings they sit around.
 *
 * `dataset.anchorX/anchorY` carries {@link markerAnchor} onto the element so the
 * engines can anchor it without knowing the kind.
 */
export function markerElement(p: MapPoint): HTMLElement {
  const el =
    p.kind === 'site'
      ? buildingMarkerElement(p)
      : p.kind === 'warning'
        ? warningMarkerElement(p)
        : pointMarkerElement(p);
  if (p.dim) el.style.opacity = '0.45';
  return el;
}

/**
 * The compact pin every non-building kind draws: coloured glyph + label chip.
 *
 * The element's LAYOUT box is only the 22px glyph square, with the label
 * positioned out of flow beside it — the building landmark's rule. MapKit
 * anchors an annotation on the bottom-centre of its element and only the y is
 * corrected (`mapkitAnchorOffset`), so when the label sat IN the box the whole
 * glyph+label strip was centred on the coordinate and the glyph landed left of
 * the true point, outside its own circle (Trav, 2026-09-30). A square box
 * makes "bottom-centre, lifted by half" the glyph's centre in both engines.
 */
function pointMarkerElement(p: MapPoint): HTMLElement {
  const a = markerAnchor(p.kind);
  const el = document.createElement('div');
  el.style.cssText = `position:relative;width:${2 * a}px;height:${2 * a}px`;
  el.innerHTML = markerGlyphHtml(p.kind);
  el.dataset.anchorX = String(a);
  el.dataset.anchorY = String(a);
  // A corner carries no label — eight "Corner" chips would bury the outline.
  if (p.label === '') return el;
  const text = document.createElement('span');
  text.textContent = p.label;
  text.style.cssText =
    `position:absolute;left:${2 * a + 6}px;top:50%;transform:translateY(-50%);` +
    'font:500 12px/1 var(--font-sans);color:var(--ink);white-space:nowrap;' +
    'background:var(--paper);padding:2px 6px;border-radius:4px;border:1px solid var(--line)';
  el.appendChild(text);
  return el;
}

/**
 * An agency's warning on the map: the kind's glyph in a tier disc, or — for a
 * cluster — the count in the same disc (the phone's `WarningPin` and
 * `WarningClusterPin`). Icon-only, because two hundred word chips buried the
 * phone's map; the headline rides `title` (hover) and `aria-label`, and a tap
 * opens the agency's own words. A square 28px layout box like the building
 * avatar, so both engines anchor its centre on the coordinate.
 */
function warningMarkerElement(p: MapPoint): HTMLElement {
  const w = p.warning ?? { tier: 'advice' as const, icon: 'other' };
  const paint = warningPinPaint(w.tier);
  const el = document.createElement('div');
  const a = markerAnchor('warning');
  el.style.cssText = `position:relative;width:${2 * a}px;height:${2 * a}px;cursor:pointer`;
  el.dataset.anchorX = String(a);
  el.dataset.anchorY = String(a);
  el.dataset.warningTier = w.tier;
  el.title = p.label;
  el.setAttribute('role', 'img');
  el.setAttribute('aria-label', p.label);
  const disc = document.createElement('span');
  disc.setAttribute('aria-hidden', 'true');
  const count = w.count ?? 0;
  disc.style.cssText =
    'position:absolute;inset:0;display:flex;align-items:center;justify-content:center;' +
    `border-radius:${count > 0 ? '14px' : '50%'};background:${paint.background};color:${paint.glyph};` +
    'border:2px solid var(--paper);box-sizing:border-box;box-shadow:var(--elev-1);' +
    'font:700 12px/1 var(--font-sans);font-variant-numeric:tabular-nums;white-space:nowrap';
  if (count > 0) {
    disc.textContent = count > 99 ? '99+' : String(count);
    // "99+" is wider than the disc; grow sideways around the same centre.
    if (count > 9) disc.style.cssText += ';left:-4px;right:-4px';
  } else {
    disc.innerHTML = warningIconSvg(w.icon, 14, paint.glyph);
  }
  el.appendChild(disc);
  return el;
}

/**
 * MapKit's `anchorOffset` for a marker: MapKit puts the element's
 * BOTTOM-CENTRE on the coordinate, and every marker's layout box is a square
 * of side `2 × markerAnchor` whose centre is the hotspot — so the only
 * correction is lifting it by half its height. x is 0 BECAUSE the box is
 * square and centred on the glyph; a marker whose box grew wider than its
 * glyph (the old in-flow label) would need an x term, and the test pins that
 * no marker kind does.
 */
export function mapkitAnchorOffset(kind: MapPoint['kind']): { x: number; y: number } {
  return { x: 0, y: -markerAnchor(kind) };
}

/**
 * The building/site landmark: a round avatar with the label centred below it,
 * reading like a place rather than a pin.
 *
 * The element's layout box is *only* the 28px avatar square, so the anchor is
 * the avatar's centre; the label is positioned out of flow beneath it and never
 * shifts that box. The avatar is a token-coloured disc that follows the map's
 * theme (`--paper`/`--ink` flip with it, legible on either basemap) and carries
 * no tier colour, because a resting building is not a state. The calm "we are
 * monitoring" pulse radiates from behind it — the same signal the old site glyph
 * carried, and never red, never live (a running building is `alerting`).
 */
function buildingMarkerElement(p: MapPoint): HTMLElement {
  const el = document.createElement('div');
  el.style.cssText = 'position:relative;width:28px;height:28px';
  const a = markerAnchor('site');
  el.dataset.anchorX = String(a);
  el.dataset.anchorY = String(a);

  const pulse = document.createElement('span');
  pulse.className = 'map-monitor-pulse';
  pulse.setAttribute('aria-hidden', 'true');
  el.appendChild(pulse);

  const avatar = document.createElement('span');
  avatar.setAttribute('aria-hidden', 'true');
  avatar.style.cssText =
    'position:relative;display:flex;align-items:center;justify-content:center;' +
    'width:28px;height:28px;border-radius:50%;background:var(--paper);color:var(--ink);' +
    'border:1px solid var(--line-strong);box-shadow:var(--elev-1);font:600 13px/1 var(--font-sans)';
  avatar.textContent = STYLE.site.glyph;
  el.appendChild(avatar);

  const label = document.createElement('span');
  label.textContent = p.label;
  label.style.cssText =
    'position:absolute;top:34px;left:50%;transform:translateX(-50%);' +
    'font:600 12px/1.15 var(--font-sans);color:var(--ink);white-space:nowrap;text-align:center;' +
    'text-shadow:0 0 3px var(--paper),0 1px 2px var(--paper),0 0 6px var(--paper)';
  el.appendChild(label);

  return el;
}

/**
 * A radius drawn on the map (migration 0111): the building's boundary
 * (`sites.geofence_radius_m`) or an assembly area (`assembly_points.radius_m`).
 * Both engines draw it the same way; the stored value is a RADIUS while the UI
 * collects a diameter and halves it (#472).
 */
/**
 * A drawn area (migration 0123): 3+ corners, unclosed on the wire — the
 * engine closes it. Painted with the same kind palette as circles, because
 * a polygon IS the circle's sibling, not a new visual category.
 */
export interface MapPolygon {
  id: string;
  /** 3+ corners draw an area; exactly 2 draw the line between them (an outline being drawn). */
  ring: { latitude: number; longitude: number }[];
  kind: 'boundary' | 'assembly' | 'warning';
  /** For `kind: 'warning'`: which tier colour the area takes. */
  tier?: WarningTier;
  /** Faded — context, not the thing being edited. */
  dim?: boolean;
}

export interface MapCircle {
  id: string;
  latitude: number;
  longitude: number;
  /** Radius in metres. */
  radiusM: number;
  kind: 'boundary' | 'assembly';
  /** Faded — context, not the thing being edited. */
  dim?: boolean;
}

/**
 * Circle colours resolved from the design tokens at draw time. Map engines
 * paint onto their own canvases/SVG where a CSS `var()` string never resolves,
 * so the computed value is read off the root element — theme-correct without
 * hardcoding a hex the token file would then drift from. The fallback only
 * exists for a non-browser test environment.
 */
function tokenColour(name: string, fallback: string): string {
  if (typeof document === 'undefined') return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
}

export function circleStyle(
  kind: MapCircle['kind'] | MapPolygon['kind'],
  tier?: WarningTier,
): {
  stroke: string;
  fill: string;
  fillOpacity: number;
} {
  // The boundary is calm context (the building's own extent); an assembly area
  // takes the accent like its marker. A warning area is an agency's statement
  // rendered ambient in the attention amber — NEVER red, which is reserved
  // for this org's own live emergency (`design-dashboard.md` §5): a weather
  // box that reads as an activation is the screen asserting a state that is
  // not true.
  // Two tiers, the pins' colours (`warningPinPaint`): severe in the attention
  // orange, advice in the yellow.
  if (kind === 'warning')
    return tier === 'advice'
      ? {
          stroke: tokenColour('--firstlight', '#f6c667'),
          fill: tokenColour('--firstlight', '#f6c667'),
          fillOpacity: 0.12,
        }
      : {
          stroke: tokenColour('--attention', '#e0912f'),
          fill: tokenColour('--attention', '#e0912f'),
          fillOpacity: 0.1,
        };
  return kind === 'boundary'
    ? {
        stroke: tokenColour('--ink-3', '#8e8e93'),
        fill: tokenColour('--ink-3', '#8e8e93'),
        fillOpacity: 0.08,
      }
    : {
        // The assembly area takes the safe green of its pin.
        stroke: tokenColour('--safe', '#1f8a4c'),
        fill: tokenColour('--safe', '#1f8a4c'),
        fillOpacity: 0.14,
      };
}

export const KIND_NAME: Record<MapPoint['kind'], string> = {
  site: 'Building',
  alerting: 'Something running',
  assembly: 'Assembly point',
  clear: 'Area clear',
  sweeping: 'Being swept',
  unswept: 'Not started',
  blocked: 'Cannot access',
  handle: 'Drag to resize',
  vertex: 'Outline corner',
  warning: 'Agency warning',
};

/**
 * Where a coordinate sits on screen right now, in viewport (client) pixels, or
 * `null` while the engine is not ready. Each engine installs one into a
 * caller's ref so a DOM surface — Home's building callout — can anchor to a
 * pin and follow it through pan and zoom, instead of floating somewhere the
 * pin is not. Read it per frame; never cache the result.
 */
/**
 * The map's visible box once the camera settles after a pan or zoom, and the
 * map's size in pixels — what a layer needs to decide what is on screen and
 * how close two pins are (the warning layer's road zoom and clustering).
 */
export interface MapRegion {
  minLatitude: number;
  maxLatitude: number;
  minLongitude: number;
  maxLongitude: number;
  widthPx: number;
  heightPx: number;
}

export type MapProjector = (latitude: number, longitude: number) => { x: number; y: number } | null;
export type MapProjectorRef = { current: MapProjector | null };
